import {
  AuthState,
  AUTH_STATE_CHANGE_EVENT,
  UI_AUTH_CHANNEL,
} from '@aws-amplify/ui-components';
import { Hub } from 'aws-amplify';
import dateFormat from 'dateformat';
import { useRouter } from 'next/navigation';
import React from 'react';
import { appPublicConfig } from '@/configs/appPublicConfig';
import type { AmplifyAuthResponse } from '@/shared/hooks/useAmplifyAuth';
import { useAmplifyAuth } from '@/shared/hooks/useAmplifyAuth';
import { useTdBridge } from '@/shared/hooks/useTdBridge';
import { useUserInfo } from '@/shared/hooks/useUserInfo';

type State = {
  pageStatus: 'INIT' | 'CONFIRM' | 'POST_PROCESS' | 'COMPLETE';
  errorMessage: string;
};

type Action =
  | {
      type: 'INITIALIZED';
    }
  | {
      type: 'API_CALLED';
      payload: {
        apiResponse: AmplifyAuthResponse;
      };
    }
  | {
      type: 'COMPLETE_POST_PROCESS';
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INITIALIZED': {
      return {
        ...state,
        pageStatus: 'CONFIRM',
      };
    }

    case 'API_CALLED': {
      const {
        apiResponse: { errorCode, errorMessage },
      } = action.payload;

      if (errorCode) {
        return {
          ...state,
          pageStatus: 'CONFIRM',
          errorMessage,
        };
      }

      return {
        ...state,
        pageStatus: 'POST_PROCESS',
      };
    }

    case 'COMPLETE_POST_PROCESS': {
      return {
        ...state,
        pageStatus: 'COMPLETE',
      };
    }
  }
};

export type UseAccountDeletion = {
  state: State;
  isLoading: boolean;
  invokeDeletion: () => Promise<void>;
};

export function useAccountDeletion() {
  const router = useRouter();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INIT',
    errorMessage: '',
  });
  const { deleteUser } = useAmplifyAuth();
  const { isLoadedUserInfo, userInfo } = useUserInfo();
  const { fetcher: tdBridgeFetcher } = useTdBridge();

  const invokeDeletion: UseAccountDeletion['invokeDeletion'] =
    React.useCallback(async () => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      const apiResponse = await deleteUser();

      dispatch({
        type: 'API_CALLED',
        payload: {
          apiResponse,
        },
      });

      setIsLoading(false);
    }, [deleteUser, isLoading]);

  React.useEffect(() => {
    switch (state.pageStatus) {
      case 'INIT': {
        if (!isLoadedUserInfo) {
          return;
        }

        if (!userInfo.isLoggedIn) {
          router.replace('/');
          return;
        }

        dispatch({
          type: 'INITIALIZED',
        });
        return;
      }

      case 'POST_PROCESS': {
        if (userInfo.isLoggedIn) {
          tdBridgeFetcher(appPublicConfig, 'user_account_log', {
            action_time: dateFormat(new Date(), 'yyyy-mm-dd HH:MM:ss'),
            user_unique_key: userInfo.customUserId,
            user_multi_account_id: null,
            super_user_flg: 0,
            action_type: 'DELETE',
          }).then(() => {
            Hub.dispatch(UI_AUTH_CHANNEL, {
              event: AUTH_STATE_CHANGE_EVENT,
              message: AuthState.SignOut,
            });
          });
          return;
        }

        dispatch({
          type: 'COMPLETE_POST_PROCESS',
        });
        return;
      }

      case 'COMPLETE': {
        return;
      }
    }
  }, [
    isLoadedUserInfo,
    router,
    state.pageStatus,
    tdBridgeFetcher,
    userInfo.customUserId,
    userInfo.isLoggedIn,
  ]);

  return {
    state,
    isLoading,
    invokeDeletion,
  };
}
